import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { UtensilsCrossed, Plus, Edit, Trash2, Search, Filter, RefreshCw, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

const EU_ALLERGENS = [
  { id: 'gluten', label: 'Gluten', emoji: '🌾' },
  { id: 'crustaceos', label: 'Crustáceos', emoji: '🦐' },
  { id: 'huevo', label: 'Huevo', emoji: '🥚' },
  { id: 'pescado', label: 'Pescado', emoji: '🐟' },
  { id: 'cacahuetes', label: 'Cacahuetes', emoji: '🥜' },
  { id: 'soja', label: 'Soja', emoji: '🫘' },
  { id: 'lacteos', label: 'Lácteos', emoji: '🥛' },
  { id: 'frutos_secos', label: 'Frutos secos', emoji: '🌰' },
  { id: 'apio', label: 'Apio', emoji: '🌿' },
  { id: 'mostaza', label: 'Mostaza', emoji: '🌻' },
  { id: 'sesamo', label: 'Sésamo', emoji: '🫚' },
  { id: 'sulfitos', label: 'Sulfitos', emoji: '🍷' },
  { id: 'altramuces', label: 'Altramuces', emoji: '🌼' },
  { id: 'moluscos', label: 'Moluscos', emoji: '🦑' },
];

const DIETARY = ['Vegano', 'Vegetariano', 'Sin gluten', 'Kosher', 'Halal', 'Sin lactosa', 'Keto', 'Bajo en sodio'];
const CATEGORIES = ['Entradas', 'Sopas', 'Ensaladas', 'Pastas', 'Carnes', 'Pescados', 'Postres', 'Bebidas', 'Otro'];

const EMPTY_ITEM = { name: '', category: 'Entradas', price: '', description: '', allergens: [], dietary_tags: [], available: true };

export default function AllergenMenuBuilder() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [guestAllergens, setGuestAllergens] = useState([]);
  const [filtered, setFiltered] = useState(null);
  const [filtering, setFiltering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/allergen-menu/items`, { headers: authHeader() });
      if (res.ok) setItems(await res.json());
    } catch { toast.error('Error al cargar menú'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAllergen = (id) => {
    setForm(p => ({
      ...p,
      allergens: p.allergens.includes(id) ? p.allergens.filter(a => a !== id) : [...p.allergens, id]
    }));
  };

  const toggleDietary = (tag) => {
    setForm(p => ({
      ...p,
      dietary_tags: p.dietary_tags.includes(tag) ? p.dietary_tags.filter(t => t !== tag) : [...p.dietary_tags, tag]
    }));
  };

  const save = async () => {
    if (!form.name) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const method = editItem ? 'PUT' : 'POST';
      const url = editItem ? `${API_URL}/api/allergen-menu/items/${editItem.id}` : `${API_URL}/api/allergen-menu/items`;
      const res = await fetch(url, { method, headers: authHeader(), body: JSON.stringify(form) });
      if (res.ok) { toast.success(editItem ? 'Platillo actualizado' : 'Platillo agregado'); setShowModal(false); load(); }
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('¿Eliminar este platillo?')) return;
    await fetch(`${API_URL}/api/allergen-menu/items/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Platillo eliminado'); load();
  };

  const filterForGuest = async () => {
    if (guestAllergens.length === 0) { setFiltered(null); return; }
    setFiltering(true);
    try {
      const res = await fetch(`${API_URL}/api/allergen-menu/filter`, {
        method: 'POST', headers: authHeader(), body: JSON.stringify({ allergens: guestAllergens })
      });
      if (res.ok) setFiltered(await res.json());
    } catch { toast.error('Error al filtrar'); }
    finally { setFiltering(false); }
  };

  const visibleItems = items.filter(item => {
    const matchSearch = item.name?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || item.category === filterCat;
    return matchSearch && matchCat;
  });

  const byCategory = visibleItems.reduce((acc, item) => {
    const cat = item.category || 'Otro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Menú con Alérgenos" subtitle="Gestiona el menú interactivo con etiquetas de alérgenos y filtrado para comensales" helpSection="allergen-menu" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        <Tabs defaultValue="menu">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <TabsList>
              <TabsTrigger value="menu">Editor de Menú</TabsTrigger>
              <TabsTrigger value="filter">Filtro para Comensales</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <div className="relative"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" /><Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-40" /></div>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-36"><SelectValue placeholder="Categoría" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todas</SelectItem>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={() => { setEditItem(null); setForm(EMPTY_ITEM); setShowModal(true); }} className="bg-orange-500 hover:bg-orange-600 text-white gap-1"><Plus className="w-4 h-4" /> Platillo</Button>
            </div>
          </div>

          <TabsContent value="menu">
            {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-orange-500 animate-spin" /></div>
            : items.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Menú vacío</p>
                <p className="text-sm">Agrega platillos con sus alérgenos para que los comensales puedan filtrar el menú</p>
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { setEditItem(null); setForm(EMPTY_ITEM); setShowModal(true); }}><Plus className="w-4 h-4 mr-1" /> Primer platillo</Button>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(byCategory).map(([cat, catItems]) => (
                  <div key={cat}>
                    <h3 className="font-bold text-gray-900 text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                      <div className="h-px flex-1 bg-gray-100" />{cat}<div className="h-px flex-1 bg-gray-100" />
                    </h3>
                    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {catItems.map(item => (
                        <Card key={item.id} className={`${!item.available ? 'opacity-50' : ''} hover:shadow-md transition-shadow`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-gray-900 truncate">{item.name}</h4>
                                {item.price && <p className="text-orange-600 font-semibold text-sm">${item.price}</p>}
                              </div>
                              <div className="flex gap-1 ml-2">
                                <Button size="sm" variant="ghost" onClick={() => { setEditItem(item); setForm(item); setShowModal(true); }}><Edit className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteItem(item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </div>
                            {item.description && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{item.description}</p>}
                            {item.allergens?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {item.allergens.map(a => {
                                  const al = EU_ALLERGENS.find(x => x.id === a);
                                  return al ? <span key={a} className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded-md">{al.emoji} {al.label}</span> : null;
                                })}
                              </div>
                            )}
                            {item.dietary_tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {item.dietary_tags.map(t => <span key={t} className="text-xs bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-md">✓ {t}</span>)}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="filter">
            <div className="max-w-2xl mx-auto space-y-6">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Filter className="w-5 h-5 text-orange-500" /> Filtrar Menú para un Comensal</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500 mb-4">Selecciona los alérgenos del comensal para ver qué platillos puede consumir de forma segura:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-4">
                    {EU_ALLERGENS.map(a => (
                      <button key={a.id} onClick={() => setGuestAllergens(prev => prev.includes(a.id) ? prev.filter(x => x !== a.id) : [...prev, a.id])}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${guestAllergens.includes(a.id) ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                        <span>{a.emoji}</span> {a.label}
                      </button>
                    ))}
                  </div>
                  <Button onClick={filterForGuest} disabled={filtering} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                    {filtering ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Filter className="w-4 h-4 mr-2" />}
                    {filtering ? 'Filtrando...' : 'Filtrar Menú'}
                  </Button>
                </CardContent>
              </Card>

              {filtered && (
                <>
                  <div>
                    <h3 className="font-bold text-emerald-600 flex items-center gap-2 mb-3"><CheckCircle className="w-5 h-5" /> Platillos SEGUROS ({filtered.safe.length})</h3>
                    <div className="space-y-2">
                      {filtered.safe.length === 0 ? <p className="text-gray-400 text-sm">No hay platillos seguros con estos alérgenos</p>
                      : filtered.safe.map(item => (
                        <div key={item.id} className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">{item.name}</p>
                            <p className="text-xs text-gray-500">{item.category}{item.price ? ` · $${item.price}` : ''}</p>
                          </div>
                          {item.dietary_tags?.map(t => <Badge key={t} className="bg-emerald-100 text-emerald-700 text-xs">{t}</Badge>)}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-red-600 flex items-center gap-2 mb-3"><XCircle className="w-5 h-5" /> Platillos con ALÉRGENOS ({filtered.unsafe.length})</h3>
                    <div className="space-y-2">
                      {filtered.unsafe.map(item => (
                        <div key={item.id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl">
                          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">{item.name}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.allergens?.filter(a => guestAllergens.includes(a)).map(a => {
                                const al = EU_ALLERGENS.find(x => x.id === a);
                                return al ? <span key={a} className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{al.emoji} {al.label}</span> : null;
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Item Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editItem ? 'Editar Platillo' : 'Nuevo Platillo'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nombre *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div><Label>Categoría</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Precio (MXN)</Label><Input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="250" /></div>
              <div className="col-span-2"><Label>Descripción</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
            </div>
            <div>
              <Label className="mb-2 block">Alérgenos (selecciona los que contiene)</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {EU_ALLERGENS.map(a => (
                  <button key={a.id} type="button" onClick={() => toggleAllergen(a.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all text-left ${form.allergens?.includes(a.id) ? 'border-red-500 bg-red-50 text-red-700 font-medium' : 'border-gray-200 text-gray-600'}`}>
                    {a.emoji} {a.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Etiquetas dietéticas</Label>
              <div className="flex flex-wrap gap-2">
                {DIETARY.map(t => (
                  <button key={t} type="button" onClick={() => toggleDietary(t)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${form.dietary_tags?.includes(t) ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-medium' : 'border-gray-200 text-gray-500'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="available" checked={form.available !== false} onChange={e => setForm(p => ({ ...p, available: e.target.checked }))} className="rounded" />
              <Label htmlFor="available">Platillo disponible</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
